import { useCallback, useMemo, useState } from "react";

const LS_KEY = "printqr:license";
const LS_EMAIL_KEY = "printqr:email";
const SS_VERIFY_PREFIX = "printqr:licenseStatus:";

type VerifyResponse = { valid: boolean; tier?: "pro" | "free" };
type ClaimResponse = { ok: boolean; token?: string; error?: string };

type CachedVerify = VerifyResponse & { ts: number };

const inflightVerify = new Map<string, Promise<VerifyResponse>>();

function readVerifyCache(token: string): VerifyResponse | null {
  try {
    const raw = sessionStorage.getItem(SS_VERIFY_PREFIX + token);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedVerify;
    if (typeof parsed?.valid !== "boolean") return null;
    return { valid: parsed.valid, tier: parsed.tier };
  } catch {
    return null;
  }
}

function writeVerifyCache(token: string, res: VerifyResponse) {
  try {
    const payload: CachedVerify = { ...res, ts: Date.now() };
    sessionStorage.setItem(SS_VERIFY_PREFIX + token, JSON.stringify(payload));
  } catch {
    // ignore storage failures (private mode / quota / disabled)
  }
}

export function useLicense() {
  const [token, setTokenState] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const isPro = useMemo(() => Boolean(token), [token]);

  // Set + persist
  const setToken = useCallback((t: string | null) => {
    setTokenState(t);
    if (t) localStorage.setItem(LS_KEY, t);
    else localStorage.removeItem(LS_KEY);
  }, []);

  // Read from localStorage then optionally verify with server
  const initFromStorage = useCallback(async (verify = true) => {
    const t = localStorage.getItem(LS_KEY);
    if (!t) {
      setTokenState(null);
      return false;
    }
    if (!verify) {
      setTokenState(t);
      return true;
    }

    const cached = readVerifyCache(t);
    if (cached) {
      if (cached.valid) {
        setTokenState(t);
        return true;
      } else {
        setToken(null);
        return false;
      }
    }

    try {
      setChecking(true);
      let p = inflightVerify.get(t);
      if (!p) {
        // Prefer new license-status endpoint; fallback to old path for compatibility
        p = (async () => {
          const r = await fetch(`/api/license-status?token=${encodeURIComponent(t)}`);
          const j: VerifyResponse = await r.json();
          writeVerifyCache(t, j);
          return j;
        })().finally(() => inflightVerify.delete(t));
        inflightVerify.set(t, p);
      }
      const j = await p;
      if (j.valid) {
        setTokenState(t);
        return true;
      } else {
        setToken(null);
        return false;
      }
    } finally {
      setChecking(false);
    }
  }, [setToken]);

  // Claim from success redirect: prefer server validation via session_id
  const claimFromUrl = useCallback(async () => {
    const sp = new URLSearchParams(window.location.search);
    const sessionId = sp.get("session_id");
    const legacyS = sp.get("s");
    if (!sessionId && !legacyS) return false;

    try {
      if (sessionId) {
        const res = await fetch(`/api/verify-session`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId })
        });
        const data: any = await res.json();
        if (data?.ok && data?.token) {
          setToken(data.token);
          if (data.email) localStorage.setItem(LS_EMAIL_KEY, data.email);
          const clean = new URL(window.location.href);
          clean.searchParams.delete("session_id");
          window.history.replaceState({}, "", clean.toString());
          // If we're on /success, send user into app (hash router)
          if (window.location.pathname === '/success') {
            const dest = new URL(window.location.href);
            dest.hash = '#/app';
            window.history.replaceState({}, "", dest.toString());
          }
          return true;
        }
      }

      if (legacyS) {
        const res = await fetch(`/api/claim-license?s=${encodeURIComponent(legacyS)}`);
        const data: ClaimResponse = await res.json();
        if (data.ok && data.token) {
          setToken(data.token);
          const clean = new URL(window.location.href);
          clean.searchParams.delete("s");
          window.history.replaceState({}, "", clean.toString());
          return true;
        }
      }
    } catch (err) {
      console.error("License activation error:", err);
    }
    return false;
  }, [setToken]);

  // Begin checkout (one-time lifetime)
  const startCheckout = useCallback(async () => {
    try {
      if (token) {
        // Already licensed; do not start checkout again
        return;
      }
      console.log("Initiating Stripe checkout...");
      const r = await fetch("/api/checkout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
      });
      const j = await r.json();
      if (j?.url) window.location.href = j.url;
      else throw new Error(j?.error || "Checkout failed");
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Checkout failed: " + (err instanceof Error ? err.message : String(err)));
    }
  }, [token]);

  const clear = useCallback(() => setToken(null), [setToken]);

  return { isPro, token, checking, setToken, initFromStorage, claimFromUrl, startCheckout, clear };
}
